// help/SettingsHelp.js

export default function SettingsHelp() {
  return (
    <div className="space-y-8">

      {/* =======================================================
          HEADER
      ======================================================== */}

      <div>
        <h2 className="text-2xl font-bold">
          Settings
        </h2>

        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
          Settings is where you manage your profile, appearance, and
          account details — including your email, password, and
          account deletion.
        </p>
      </div>

      {/* =======================================================
          PROFILE PHOTO
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Updating Your Profile Photo
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Open <strong>Settings</strong>.
          </li>

          <li>
            Tap your avatar to view it full-size, or tap
            <strong> Edit</strong> to choose a new photo.
          </li>

          <li>
            Select an image from your device. It's resized
            automatically before uploading.
          </li>

          <li>
            Your new photo updates immediately across the app.
          </li>
        </ul>
      </section>

      {/* =======================================================
          USERNAME
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Changing Your Username
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Open <strong>Settings</strong> and tap your username.
          </li>

          <li>
            Enter a new username.
          </li>

          <li>
            Tap <strong>Save</strong> to confirm, or
            <strong> Cancel</strong> to discard your changes.
          </li>
        </ul>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Your username is how other people find and identify your
          account.
        </p>
        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
  Usernames must be 3–15 characters, start with a letter, and
  contain only letters, numbers, and underscores. Usernames
  that look like phone numbers, or that impersonate staff
  (e.g. starting with "admin", "support", "system", "help"),
  aren't allowed.
</p>
      </section>

      {/* =======================================================
          DISPLAY NAME
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Changing Your Display Name
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Open <strong>Settings</strong> and tap your display name.
          </li>

          <li>
            Enter a new display name.
          </li>

          <li>
            Tap <strong>Save</strong> to confirm, or
            <strong> Cancel</strong> to discard your changes.
          </li>
        </ul>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Your display name appears alongside your username in chats,
          posts, and comments.
        </p>
      </section>

      {/* =======================================================
          THEME
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Switching Light and Dark Mode
        </h3>

        <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
          <li>
            Open <strong>Settings</strong>.
          </li>

          <li>
            Tap the 🌙 / ☀️ button at the top of the screen to switch
            between light and dark mode.
          </li>
        </ul>
      </section>

      {/* =======================================================
          ACCOUNT — EMAIL & PASSWORD
      ======================================================== */}

      <section>
        <h3 className="text-lg font-semibold">
          Changing Your Email or Password
        </h3>

        <ol className="mt-3 list-decimal pl-6 space-y-2 text-sm">
          <li>
            Open <strong>Settings</strong> and select
            <strong> Change email &amp; password</strong>.
          </li>

          <li>
            Update your email if needed.
          </li>

          <li>
            Enter your <strong>current password</strong> — this is
            always required to save any change on this screen.
          </li>

          <li>
            To change your password, also enter a
            <strong> new password</strong> and confirm it.
          </li>

          <li>
            Select <strong>Save changes</strong>.
          </li>
        </ol>

        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
          Use the <strong>Show</strong> / <strong>Hide</strong> links
          next to each password field to check what you've typed before
          saving. If you change your password, your encrypted keys are
          automatically re-secured with the new password.
        </p>
        <p className="mt-3 text-neutral-600 dark:text-neutral-400">
  A new password must be at least 8 characters, and include
  at least one letter, one number, and one special character.
  You'll need to enter it twice to confirm.
</p>
      </section>

     {/* =======================================================
    DELETE ACCOUNT
======================================================== */}

<section>
  <h3 className="text-lg font-semibold">
    Deleting Your Account
  </h3>

  <ol className="mt-3 list-decimal pl-6 space-y-2 text-sm">
    <li>
      Open <strong>Settings</strong> →
      <strong> Change email &amp; password</strong>.
    </li>

    <li>
      Scroll down and select <strong>Delete account</strong>.
    </li>

    <li>
      Enter your current password to confirm.
    </li>

    <li>
      Select <strong>Delete</strong>.
    </li>
  </ol>

  <p className="mt-3 text-neutral-600 dark:text-neutral-400">
    Deleting your account is <strong>permanent</strong> — your
    profile, USWD balance, and USDT balance are all erased and
    cannot be recovered.
  </p>

  <p className="mt-3 text-neutral-600 dark:text-neutral-400">
    Before you can delete your account, it needs to be
    completely clean. Deletion is blocked if any of the
    following apply:
  </p>

  <ul className="mt-3 list-disc pl-6 space-y-2 text-sm">
    <li>
      You have a <strong>USWD or USDT balance</strong> —
      withdraw or transfer it first.
    </li>
    <li>
      You have an <strong>active ad</strong> listed — remove
      or complete it first.
    </li>
    <li>
      You have an <strong>open P2P order</strong> (pending or
      paid), as either buyer or seller — complete or cancel
      it first.
    </li>
    <li>
      You have <strong>funds held in escrow</strong>, as
      either buyer or seller — complete or cancel that trade
      first.
    </li>
    <li>
      You have a <strong>refundable rent deposit</strong> —
      if so, you'll be prompted to refund it before deletion
      can continue.
    </li>
  </ul>

  <p className="mt-3 text-neutral-600 dark:text-neutral-400">
    If any of these apply, you'll see a message explaining
    what to resolve first. Once everything is cleared, you
    can delete your account immediately.
  </p>
</section>
    </div>
  );
}